import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Form, Modal, Nav, Spinner, Tab, Table } from 'react-bootstrap'
import { useLazyQuery, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import axios from 'axios'
import * as XLSX from 'xlsx'

import { URL as BACKEND_URL, fetchNewToken } from '../../gql/client'
import { STORAGE_NAMES } from '../../helpers/consts'
import { GET_ALL_LIST_LEADS } from '../../gql/queries/leadsQuery'
import { GET_ALL_PIPELINES } from '../../gql/queries/pipelines'
import { IResPipelines, IStagePipelineSet } from '../../types/pipelines'
import { parseId } from '../../helpers'
import { ImportFromFilePanel } from './ImportFromFilePanel'
import { useIntl, FormattedMessage } from 'react-intl'
import {
  CAMPAIGN_MESSAGES as M,
  DIALER_POLICYDIAL,
  PRODUCT_POLICYTOOLS,
  REQUIRED_MARK,
  listOption,
  vdTag,
} from './campaignMessages'

// Header names are chosen to match ImportFromFilePanel.guessField() exactly so a
// freshly downloaded template gets auto-mapped on upload without manual mapping.
const downloadLeadsTemplate = () => {
  const sampleRows = [
    { phone: '4045551234', first_name: 'John', last_name: 'Doe',
      email: 'john@example.com', state: 'GA', postal_code: '30303',
      comments: 'Spoke to spouse 5/20, prefers afternoons' },
    { phone: '13055551234', first_name: 'Jane', last_name: 'Smith',
      email: 'jane@example.com', state: 'FL', postal_code: '33101',
      comments: '' },
  ]
  const ws = XLSX.utils.json_to_sheet(sampleRows, {
    header: ['phone', 'first_name', 'last_name', 'email', 'state', 'postal_code', 'comments'],
  })
  ws['!cols'] = [
    { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 26 }, { wch: 8 }, { wch: 12 }, { wch: 40 },
  ]
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Leads')
  XLSX.writeFile(wb, 'policydial_leads_template.xlsx')
}

type SourceTab = 'policytools' | 'file'

interface VicidialCampaign {
  campaign_id: string
  campaign_name: string
  active: string
  dial_method: string
  dial_level: string
}

interface LeadNode {
  id: string
  name: string
  phone: string
  stagePipeline?: { id: string; name: string }
  tagInsurance?: { edges: { node: { id: string; name: string; color: string } }[] }
}

interface PushResult {
  lead_id: string
  lead_name?: string
  status: 'added' | 'failed' | 'skipped'
  reason?: string
  vicidial_lead_id?: string
}

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem(STORAGE_NAMES.TOKEN) || ''}`,
})

const authPost = async (url: string, data: any) => {
  try {
    return await axios.post(url, data, { headers: getAuthHeaders() })
  } catch (err: any) {
    if (err?.response?.status === 401) {
      const newToken = await fetchNewToken()
      if (newToken) {
        return await axios.post(url, data, {
          headers: { Authorization: `Bearer ${newToken}` },
        })
      }
    }
    throw err
  }
}

const PAGE_SIZE = 50

export const VicidialCampaignsPage = () => {
  const intl = useIntl()
  // Campaign list
  const [campaigns, setCampaigns] = useState<VicidialCampaign[]>([])
  const [loadingCampaigns, setLoadingCampaigns] = useState(false)
  const [campaignsError, setCampaignsError] = useState('')
  const [selectedCampaignId, setSelectedCampaignId] = useState('')

  // Lists for the selected campaign — required so VICIdial puts leads in the
  // right hopper instead of falling back to an arbitrary default list.
  const [lists, setLists] = useState<{list_id: string; list_name: string; campaign_id: string}[]>([])
  const [loadingLists, setLoadingLists] = useState(false)
  const [selectedListId, setSelectedListId] = useState('')

  // Filters
  const [pipelineId, setPipelineId] = useState('')
  const [stageId, setStageId] = useState('')
  const [searchName, setSearchName] = useState('')
  const [searchPhone, setSearchPhone] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  // Lead results & selection
  const [selectedLeads, setSelectedLeads] = useState<LeadNode[]>([])
  const [selectAll, setSelectAll] = useState(false)

  // Push modal
  const [showPushModal, setShowPushModal] = useState(false)
  const [pushing, setPushing] = useState(false)
  const [pushResults, setPushResults] = useState<PushResult[] | null>(null)

  // Source tab: 'policytools' (default) or 'file'
  const [sourceTab, setSourceTab] = useState<SourceTab>('policytools')

  // Locked while ImportFromFilePanel has a push in flight so switching tabs
  // doesn't unmount the panel and abort the upload mid-way.
  const [fileImportPushing, setFileImportPushing] = useState(false)

  // Pipelines query
  const { data: pipelinesData } = useQuery<IResPipelines>(GET_ALL_PIPELINES, {
    fetchPolicy: 'cache-first',
  })

  const pipelines = pipelinesData?.allPipelines?.edges || []
  const stages: IStagePipelineSet[] = useMemo(() => {
    if (!pipelineId) return []
    const pipeline = pipelines.find(({ node }) => node.id === pipelineId)
    return pipeline?.node.stagePipelineSet?.edges || []
  }, [pipelineId, pipelines])

  // Auto-select first pipeline
  useEffect(() => {
    if (!pipelineId && pipelines.length > 0) {
      setPipelineId(pipelines[0].node.id)
    }
  }, [pipelines, pipelineId])

  // Leads query
  const [fetchLeads, { data: leadsData, loading: loadingLeads }] = useLazyQuery(GET_ALL_LIST_LEADS, {
    fetchPolicy: 'network-only',
  })

  const totalLeads: number = leadsData?.totalLeads ?? 0
  const leadResults: LeadNode[] = useMemo(() => {
    return (leadsData?.allLeads?.edges || []).map((e: any) => e.node)
  }, [leadsData])

  const doSearch = useCallback(() => {
    if (!pipelineId) return
    setSelectAll(false)
    setSelectedLeads([])
    fetchLeads({
      variables: {
        pipelineId: pipelineId ? parseInt(parseId(pipelineId) as any) : undefined,
        stagePipelineId: stageId ? parseInt(parseId(stageId) as any) : undefined,
        name_Icontains: searchName || undefined,
        phone_Icontains: searchPhone || undefined,
        fromDate: dateFrom || undefined,
        toDate: dateTo || undefined,
        first: PAGE_SIZE,
        offset: 0,
      },
    })
  }, [pipelineId, stageId, searchName, searchPhone, dateFrom, dateTo, fetchLeads])

  // Search on filter change
  useEffect(() => {
    if (pipelineId) doSearch()
  }, [pipelineId, stageId, doSearch])

  // Campaign fetch
  const fetchCampaigns = useCallback(async () => {
    setLoadingCampaigns(true)
    setCampaignsError('')
    try {
      const response = await authPost(`${BACKEND_URL}/api/telephony/vicidial/campaigns`, {})
      setCampaigns(response.data.campaigns || [])
    } catch (err: any) {
      setCampaignsError(err?.response?.data?.error || err.message || 'Failed to load campaigns')
    } finally {
      setLoadingCampaigns(false)
    }
  }, [])

  useEffect(() => {
    fetchCampaigns()
  }, [fetchCampaigns])

  // Refresh the lists dropdown whenever the campaign changes. Auto-select
  // when there's exactly one match; clear stale selection otherwise.
  useEffect(() => {
    if (!selectedCampaignId) {
      setLists([])
      setSelectedListId('')
      return
    }
    let cancelled = false
    setLoadingLists(true)
    axios
      .get(
        `${BACKEND_URL}/api/telephony/vicidial/lists?campaign_id=${encodeURIComponent(selectedCampaignId)}`,
        { headers: getAuthHeaders() }
      )
      .then((resp) => {
        if (cancelled) return
        const fetched = resp.data?.lists || []
        setLists(fetched)
        if (fetched.length === 1) {
          setSelectedListId(fetched[0].list_id)
        } else if (!fetched.some((l: any) => l.list_id === selectedListId)) {
          setSelectedListId('')
        }
      })
      .catch(() => {
        if (!cancelled) setLists([])
      })
      .finally(() => {
        if (!cancelled) setLoadingLists(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaignId])

  const selectedCampaign = useMemo(
    () => campaigns.find((c) => c.campaign_id === selectedCampaignId),
    [campaigns, selectedCampaignId]
  )

  // Selection helpers
  const toggleLead = (lead: LeadNode) => {
    setSelectedLeads((prev) => {
      const exists = prev.find((l) => l.id === lead.id)
      if (exists) return prev.filter((l) => l.id !== lead.id)
      return [...prev, lead]
    })
  }

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedLeads([])
      setSelectAll(false)
    } else {
      setSelectedLeads([...leadResults])
      setSelectAll(true)
    }
  }

  // Push
  const handlePushLeads = async () => {
    if (!selectedCampaignId || selectedLeads.length === 0) return
    setPushing(true)
    setPushResults(null)
    try {
      const response = await authPost(
        `${BACKEND_URL}/api/telephony/vicidial/campaigns/push-leads`,
        {
          campaign_id: selectedCampaignId,
          campaign_name: selectedCampaign?.campaign_name || '',
          list_id: selectedListId,
          lead_ids: selectedLeads.map((l) => parseId(l.id)),
        }
      )
      setPushResults(response.data.results || [])
      const { added, failed, skipped } = response.data
      if (added > 0)
        toast.success(intl.formatMessage(M.toastLeadsAdded, {count: added, dialer: DIALER_POLICYDIAL}))
      if (failed > 0) toast.warning(intl.formatMessage(M.toastLeadsFailed, {count: failed}))
      if (skipped > 0) toast.info(intl.formatMessage(M.toastLeadsSkipped, {count: skipped}))
    } catch (err: any) {
      toast.error(
        intl.formatMessage(M.errorWithMessage, {
          message: err?.response?.data?.error || err.message,
        })
      )
    } finally {
      setPushing(false)
    }
  }

  // Push by filters (all matching leads, not just current page)
  const handlePushByFilters = async () => {
    if (!selectedCampaignId) return
    setPushing(true)
    setPushResults(null)
    try {
      const response = await authPost(
        `${BACKEND_URL}/api/telephony/vicidial/campaigns/push-filtered-leads`,
        {
          campaign_id: selectedCampaignId,
          campaign_name: selectedCampaign?.campaign_name || '',
          list_id: selectedListId,
          pipeline_id: pipelineId ? parseId(pipelineId) : undefined,
          stage_pipeline_ids: stageId ? [parseId(stageId)] : undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          search_name: searchName || undefined,
          search_phone: searchPhone || undefined,
        }
      )
      setPushResults(response.data.results || [])
      const { added, failed, skipped } = response.data
      if (added > 0)
        toast.success(intl.formatMessage(M.toastLeadsAdded, {count: added, dialer: DIALER_POLICYDIAL}))
      if (failed > 0) toast.warning(intl.formatMessage(M.toastLeadsFailed, {count: failed}))
      if (skipped > 0) toast.info(intl.formatMessage(M.toastLeadsSkipped, {count: skipped}))
    } catch (err: any) {
      toast.error(
        intl.formatMessage(M.errorWithMessage, {
          message: err?.response?.data?.error || err.message,
        })
      )
    } finally {
      setPushing(false)
    }
  }

  return (
    <div className='vicidial-campaigns-page'>
      <div className='d-flex align-items-center justify-content-between mb-6 flex-wrap gap-3'>
        <div>
          <h1 className='page-title'>
              <FormattedMessage {...M.dialerTitle} values={{dialer: DIALER_POLICYDIAL}} />
            </h1>
          <div className='text-muted'>
              <FormattedMessage
                {...M.dialerSubtitle}
                values={{product: PRODUCT_POLICYTOOLS, dialer: DIALER_POLICYDIAL}}
              />
            </div>
        </div>
        <div className='d-flex gap-2'>
          <Button variant='outline-primary' size='sm' onClick={downloadLeadsTemplate}>
            <i className='bi bi-download me-1'></i> <FormattedMessage {...M.dialerDownloadTemplate} />
          </Button>
          <Button variant='outline-secondary' size='sm' onClick={fetchCampaigns} disabled={loadingCampaigns}>
            <i className='bi bi-arrow-clockwise me-1'></i> <FormattedMessage {...M.refresh} />
          </Button>
        </div>
      </div>

      {campaignsError && <Alert variant='danger'>{campaignsError}</Alert>}

      <div className='row g-5'>
        {/* Left: Campaign List */}
        <div className='col-xxl-3 col-xl-3'>
          <div className='card shadow-sm'>
            <div className='card-header border-bottom bg-light'>
              <h5 className='card-title m-0'>
                <i className='bi bi-telephone-outbound me-2 text-primary'></i>
                <FormattedMessage {...M.navCampaigns} />
              </h5>
            </div>
            <div className='card-body p-0'>
              {loadingCampaigns ? (
                <div className='text-center p-5'>
                  <Spinner animation='border' size='sm' />
                  <div className='text-muted mt-2 small'>{intl.formatMessage(M.loading)}</div>
                </div>
              ) : campaigns.length === 0 ? (
                <div className='text-center p-5 text-muted'>
                  <i className='bi bi-megaphone fs-1 d-block mb-2 opacity-25'></i>
                  <FormattedMessage {...M.dialerNoCampaigns} />
                </div>
              ) : (
                <div className='list-group list-group-flush' style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  {campaigns.map((c) => (
                    <button
                      key={c.campaign_id}
                      type='button'
                      className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                        selectedCampaignId === c.campaign_id ? 'active' : ''
                      }`}
                      onClick={() => setSelectedCampaignId(c.campaign_id)}
                    >
                      <div>
                        <div className='fw-bold small'>{c.campaign_name || c.campaign_id}</div>
                        <small className={selectedCampaignId === c.campaign_id ? 'text-white-50' : 'text-muted'} style={{ fontSize: '0.7rem' }}>
                          {c.campaign_id}
                        </small>
                      </div>
                      <Badge bg={c.active === 'Y' ? 'success' : 'secondary'} style={{ fontSize: '0.65rem' }}>
                        {intl.formatMessage(c.active === 'Y' ? M.dialerActive : M.dialerOff)}
                      </Badge>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Filters + Lead results */}
        <div className='col-xxl-9 col-xl-9'>
          {!selectedCampaignId ? (
            <div className='card shadow-sm'>
              <div className='card-body text-center p-10 text-muted'>
                <i className='bi bi-arrow-left-circle fs-1 d-block mb-3 opacity-25'></i>
                <FormattedMessage {...M.dialerSelectCampaign} values={{dialer: DIALER_POLICYDIAL}} />
              </div>
            </div>
          ) : (
            <Tab.Container
              activeKey={sourceTab}
              onSelect={(k) => {
                if (fileImportPushing) return
                setSourceTab((k as SourceTab) || 'policytools')
              }}
            >
              <div className='card shadow-sm'>
                <div className='card-header border-bottom bg-light d-flex justify-content-between align-items-center flex-wrap gap-2'>
                  <div className='d-flex align-items-center flex-wrap gap-3'>
                    <h5 className='card-title m-0'>
                      <i className='bi bi-people me-2 text-info'></i>
                      <FormattedMessage {...M.dialerAddLeadsTo} /> <span className='text-primary'>{selectedCampaign?.campaign_name}</span>
                    </h5>
                    {/* Target list — global so it's respected by every push flow (filtered, selected, file). */}
                    <div className='d-flex align-items-center gap-2'>
                      <label className='small text-muted mb-0'>
                        <FormattedMessage {...M.dialerTargetList} /> <span className='text-danger'>{REQUIRED_MARK}</span>
                      </label>
                      <Form.Select
                        size='sm'
                        style={{ minWidth: 200 }}
                        value={selectedListId}
                        onChange={(e) => setSelectedListId(e.target.value)}
                        disabled={loadingLists || lists.length === 0}
                      >
                        {loadingLists && <option value=''>{intl.formatMessage(M.dialerLoadingLists)}</option>}
                        {!loadingLists && lists.length === 0 && (
                          <option value=''>{intl.formatMessage(M.dialerNoLists)}</option>
                        )}
                        {!loadingLists && lists.length > 0 && (
                          <>
                            <option value=''>{intl.formatMessage(M.dialerSelectList)}</option>
                            {lists.map((l) => (
                              <option key={l.list_id} value={l.list_id}>
                                {listOption(l.list_id, l.list_name)}
                              </option>
                            ))}
                          </>
                        )}
                      </Form.Select>
                    </div>
                    <Nav variant='tabs' className='border-0'>
                      <Nav.Item>
                        <Nav.Link
                          eventKey='policytools'
                          className='py-1 px-3 small'
                          disabled={fileImportPushing}
                          title={fileImportPushing ? intl.formatMessage(M.dialerWaitImport) : undefined}
                        >
                          <i className='bi bi-funnel me-1'></i> <FormattedMessage {...M.dialerFromProduct} values={{product: PRODUCT_POLICYTOOLS}} />
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey='file' className='py-1 px-3 small' disabled={fileImportPushing}>
                          <i className='bi bi-file-earmark-arrow-up me-1'></i> <FormattedMessage {...M.dialerImportFromFile} />
                        </Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </div>
                  {sourceTab === 'policytools' && (
                    <div className='d-flex gap-2'>
                      {totalLeads > 0 && (
                        <Button
                          variant='outline-primary'
                          size='sm'
                          disabled={pushing || (lists.length > 0 && !selectedListId)}
                          onClick={() => { setShowPushModal(true); setPushResults(null) }}
                        >
                          <i className='bi bi-funnel me-1'></i>
                          <FormattedMessage {...M.dialerPushFiltered} values={{count: totalLeads.toLocaleString()}} />
                        </Button>
                      )}
                      <Button
                        variant='primary'
                        size='sm'
                        disabled={selectedLeads.length === 0 || (lists.length > 0 && !selectedListId)}
                        onClick={() => { setShowPushModal(true); setPushResults(null) }}
                      >
                        <i className='bi bi-send me-1'></i>
                        <FormattedMessage {...M.dialerPushSelected} values={{count: selectedLeads.length}} />
                      </Button>
                    </div>
                  )}
                </div>
                <div className='card-body'>
                  <Tab.Content>
                    <Tab.Pane eventKey='policytools'>
                {/* Filters */}
                <div className='row g-3 mb-4'>
                  <div className='col-md-3'>
                    <Form.Label className='small fw-bold text-muted mb-1'><FormattedMessage {...M.dialerPipeline} /></Form.Label>
                    <Form.Select
                      size='sm'
                      value={pipelineId}
                      onChange={(e) => { setPipelineId(e.target.value); setStageId('') }}
                    >
                      <option value=''>{intl.formatMessage(M.dialerAllPipelines)}</option>
                      {pipelines.map(({ node }) => (
                        <option key={node.id} value={node.id}>{node.name}</option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className='col-md-3'>
                    <Form.Label className='small fw-bold text-muted mb-1'><FormattedMessage {...M.dialerStage} /></Form.Label>
                    <Form.Select
                      size='sm'
                      value={stageId}
                      onChange={(e) => setStageId(e.target.value)}
                      disabled={!pipelineId}
                    >
                      <option value=''>{intl.formatMessage(M.dialerAllStages)}</option>
                      {stages.map(({ node }) => (
                        <option key={node.id} value={node.id}>{node.name}</option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className='col-md-2'>
                    <Form.Label className='small fw-bold text-muted mb-1'><FormattedMessage {...M.dialerFrom} /></Form.Label>
                    <Form.Control
                      size='sm'
                      type='date'
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </div>
                  <div className='col-md-2'>
                    <Form.Label className='small fw-bold text-muted mb-1'><FormattedMessage {...M.dialerTo} /></Form.Label>
                    <Form.Control
                      size='sm'
                      type='date'
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </div>
                  <div className='col-md-2 d-flex align-items-end'>
                    <Button variant='secondary' size='sm' className='w-100' onClick={doSearch}>
                      <i className='bi bi-search me-1'></i> <FormattedMessage {...M.search} />
                    </Button>
                  </div>
                </div>

                {/* Name / Phone search row */}
                <div className='row g-3 mb-4'>
                  <div className='col-md-5'>
                    <div className='input-group input-group-sm'>
                      <span className='input-group-text bg-white'><i className='bi bi-person'></i></span>
                      <Form.Control
                        size='sm'
                        type='text'
                        placeholder={intl.formatMessage(M.dialerLeadNamePlaceholder)}
                        value={searchName}
                        onChange={(e) => setSearchName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && doSearch()}
                      />
                    </div>
                  </div>
                  <div className='col-md-5'>
                    <div className='input-group input-group-sm'>
                      <span className='input-group-text bg-white'><i className='bi bi-telephone'></i></span>
                      <Form.Control
                        size='sm'
                        type='text'
                        placeholder={intl.formatMessage(M.dialerPhonePlaceholder)}
                        value={searchPhone}
                        onChange={(e) => setSearchPhone(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && doSearch()}
                      />
                    </div>
                  </div>
                  <div className='col-md-2'>
                    <Button
                      variant='outline-secondary'
                      size='sm'
                      className='w-100'
                      onClick={() => {
                        setStageId('')
                        setSearchName('')
                        setSearchPhone('')
                        setDateFrom('')
                        setDateTo('')
                      }}
                    >
                      <i className='bi bi-x-circle me-1'></i> <FormattedMessage {...M.fileClear} />
                    </Button>
                  </div>
                </div>

                {/* Results count */}
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <div className='d-flex align-items-center gap-3'>
                    <small className='text-muted fw-bold'>
                      {loadingLeads
                        ? intl.formatMessage(M.dialerSearching)
                        : intl.formatMessage(M.dialerLeadsFound, {count: totalLeads.toLocaleString()})}
                    </small>
                    {loadingLeads && <Spinner animation='border' size='sm' />}
                  </div>
                  <small className='text-muted'>
                    {selectedLeads.length > 0 && (
                      <Badge bg='primary' className='me-2'>
                        {intl.formatMessage(M.dialerSelectedCount, {count: selectedLeads.length})}
                      </Badge>
                    )}
                  </small>
                </div>

                {/* Results table */}
                <div className='border rounded' style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                  <Table hover size='sm' className='mb-0'>
                    <thead className='table-light sticky-top'>
                      <tr>
                        <th style={{ width: 35 }}>
                          <Form.Check
                            type='checkbox'
                            checked={selectAll && leadResults.length > 0}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th><FormattedMessage {...M.name} /></th>
                        <th><FormattedMessage {...M.phone} /></th>
                        <th><FormattedMessage {...M.dialerStage} /></th>
                        <th><FormattedMessage {...M.tags} /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {leadResults.length === 0 && !loadingLeads ? (
                        <tr>
                          <td colSpan={5} className='text-center text-muted py-5'>
                            <i className='bi bi-search fs-3 d-block mb-2 opacity-25'></i>
                            <FormattedMessage {...M.dialerNoLeads} />
                          </td>
                        </tr>
                      ) : (
                        leadResults.map((lead) => {
                          const isSelected = selectedLeads.some((l) => l.id === lead.id)
                          return (
                            <tr
                              key={lead.id}
                              onClick={() => toggleLead(lead)}
                              style={{ cursor: 'pointer' }}
                              className={isSelected ? 'table-primary' : ''}
                            >
                              <td>
                                <Form.Check
                                  type='checkbox'
                                  checked={isSelected}
                                  onChange={() => toggleLead(lead)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td className='fw-bold'>{lead.name}</td>
                              <td className='text-muted small'>{lead.phone}</td>
                              <td>
                                {lead.stagePipeline && (
                                  <Badge bg='light' text='dark' className='border' style={{ fontSize: '0.7rem' }}>
                                    {lead.stagePipeline.name}
                                  </Badge>
                                )}
                              </td>
                              <td>
                                <div className='d-flex gap-1 flex-wrap'>
                                  {lead.tagInsurance?.edges?.map(({ node: tag }) => (
                                    <span
                                      key={tag.id}
                                      className='badge'
                                      style={{
                                        backgroundColor: tag.color || '#6c757d',
                                        color: '#fff',
                                        fontSize: '0.65rem',
                                      }}
                                    >
                                      {tag.name}
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
                    </Tab.Pane>
                    <Tab.Pane eventKey='file'>
                      <ImportFromFilePanel
                        campaignId={selectedCampaignId}
                        campaignName={selectedCampaign?.campaign_name || selectedCampaignId}
                        listId={selectedListId}
                        hasLists={lists.length > 0}
                        onPushingChange={setFileImportPushing}
                      />
                    </Tab.Pane>
                  </Tab.Content>
                </div>
              </div>
            </Tab.Container>
          )}
        </div>
      </div>

      {/* Push Modal */}
      <Modal show={showPushModal} onHide={() => { setShowPushModal(false); setPushResults(null) }} centered size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            {pushResults
              ? intl.formatMessage(M.dialerPushResults)
              : intl.formatMessage(M.dialerConfirmPush, {dialer: DIALER_POLICYDIAL})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pushResults ? (
            <div>
              <div className='d-flex gap-3 mb-4'>
                <div className='bg-light rounded p-3 text-center flex-fill'>
                  <div className='fs-2 fw-bold text-success'>{pushResults.filter((r) => r.status === 'added').length}</div>
                  <small className='text-muted'><FormattedMessage {...M.fileStatAdded} /></small>
                </div>
                <div className='bg-light rounded p-3 text-center flex-fill'>
                  <div className='fs-2 fw-bold text-danger'>{pushResults.filter((r) => r.status === 'failed').length}</div>
                  <small className='text-muted'><FormattedMessage {...M.statFailed} /></small>
                </div>
                <div className='bg-light rounded p-3 text-center flex-fill'>
                  <div className='fs-2 fw-bold text-secondary'>{pushResults.filter((r) => r.status === 'skipped').length}</div>
                  <small className='text-muted'><FormattedMessage {...M.fileStatSkipped} /></small>
                </div>
              </div>
              {pushResults.filter((r) => r.status !== 'added').length > 0 && (
                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  <small className='text-muted fw-bold d-block mb-2'><FormattedMessage {...M.fileIssues} /></small>
                  {pushResults.filter((r) => r.status !== 'added').map((r, i) => (
                    <div key={i} className='d-flex justify-content-between align-items-center border-bottom py-2'>
                      <span className='small'>{r.lead_name || r.lead_id}</span>
                      <Badge bg={r.status === 'failed' ? 'danger' : 'secondary'} className='small'>
                        {r.reason || r.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <p>
                <FormattedMessage
                  {...M.dialerPushLeadsTo}
                  values={{
                    campaign: selectedCampaign?.campaign_name ?? '',
                    b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
                  }}
                />
              </p>
              <div className='d-flex gap-3 mb-3'>
                {selectedLeads.length > 0 && (
                  <Button
                    variant={selectedLeads.length > 0 ? 'primary' : 'outline-primary'}
                    className='flex-fill py-3'
                    onClick={handlePushLeads}
                    disabled={pushing}
                  >
                    {pushing ? <Spinner animation='border' size='sm' /> : (
                      <>
                        <i className='bi bi-check2-square d-block fs-3 mb-1'></i>
                        <FormattedMessage {...M.dialerPushSelected} values={{count: selectedLeads.length}} />
                      </>
                    )}
                  </Button>
                )}
                {totalLeads > 0 && (
                  <Button
                    variant='outline-primary'
                    className='flex-fill py-3'
                    onClick={handlePushByFilters}
                    disabled={pushing}
                  >
                    {pushing ? <Spinner animation='border' size='sm' /> : (
                      <>
                        <i className='bi bi-funnel d-block fs-3 mb-1'></i>
                        <FormattedMessage {...M.dialerPushFiltered} values={{count: totalLeads.toLocaleString()}} />
                      </>
                    )}
                  </Button>
                )}
              </div>
              <Alert variant='info' className='small mb-0'>
                <i className='bi bi-info-circle me-1'></i>
                <FormattedMessage
                  {...M.dialerHopperHint}
                  values={{
                    dialer: DIALER_POLICYDIAL,
                    tag: (
                      <Badge bg='light' text='dark' className='border'>
                        {vdTag(selectedCampaign?.campaign_name)}
                      </Badge>
                    ),
                  }}
                />
              </Alert>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {pushResults ? (
            <Button variant='secondary' onClick={() => { setShowPushModal(false); setPushResults(null); setSelectedLeads([]); setSelectAll(false) }}>
              <FormattedMessage {...M.close} />
            </Button>
          ) : (
            <Button variant='secondary' onClick={() => setShowPushModal(false)}>
              <FormattedMessage {...M.cancel} />
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  )
}
